import {
  Box,
  Grid,
  Divider,
  makeStyles,
  CircularProgress
} from '@material-ui/core';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';

import { SETVIImage, SHighlightText, SText } from '@setvi/shared/components';
import {
  ProductPriceType,
  Product as ProductType
} from '@setvi/shared/interfaces';
import { distances } from '@setvi/shared/styles';
import { TextColor } from '@setvi/shared/enums';
import { PriceButton } from 'pages/products/components/price-button';
import { getProductStatus } from 'componentsV2/product/status';
import { Customer } from 'Services/MongoRealm/Types';
import { Price } from '../../../useProductColumns/contents';
import {
  Container,
  ManufacturerContainer,
  PriceContainer,
  PriceItem,
  TitleContainer
} from './styled';

export const useProductsStyles = makeStyles(({ spacing }) => ({
  container: {
    padding: distances.px.base,
    borderBottom: `1px solid ${TextColor.LightGrey}`
  },
  wrapper: {
    paddingBottom: spacing(2),
    marginBottom: spacing(2),
    borderBottom: '1px solid #E6E7E9',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  img: {
    borderRadius: 12,
    border: '1px solid #AFBAC6',
    width: 68,
    height: 68
  },
  removeButton: {
    cursor: 'pointer'
  }
}));

interface ProductProps {
  product: ProductType;
  searchQuery?: string;
  priceLoading?: boolean;
  priceType: ProductPriceType;
  selectedCustomer: Customer;
  removeProduct: (product: ProductType) => void;
  getProductPrice: (product: ProductType[]) => Promise<void>;
}

export const Product = ({
  product,
  searchQuery,
  priceType,
  priceLoading,
  selectedCustomer,
  removeProduct,
  getProductPrice
}: ProductProps) => {
  const classes = useProductsStyles();
  const showEmptyGrid =
    (priceType === ProductPriceType.Get && selectedCustomer) ||
    (priceType === ProductPriceType.GetFull && selectedCustomer) ||
    (priceType === ProductPriceType.Full && selectedCustomer) ||
    priceType === ProductPriceType.List;

  return (
    <Container>
      <Box width="100%">
        <Box width="100%" display="flex" gridGap={12} alignItems="center">
          <SETVIImage
            className={classes.img}
            src={
              product.imageUrl ||
              product.images[0]?.path ||
              '/images/product-placeholder.svg'
            }
            alt={product.name}
          />
          <TitleContainer>
            <SHighlightText
              value={product.name}
              searchQuery={searchQuery}
              maxLines={2}
              weight="semibold"
            />

            <Box display="flex" gridGap={4}>
              <SHighlightText
                value={`SKU: ${product?.sku}`}
                searchQuery={searchQuery}
                fontColor={TextColor.Grey}
                size="sm"
              />
              <SText
                fontColor={TextColor.Grey}
                size="sm"
                title={`(${getProductStatus(product)})`}
              />
            </Box>
          </TitleContainer>
        </Box>
        <ManufacturerContainer>
          <SHighlightText
            value={
              product?.vendor?.name ? `MFG: ${product?.vendor?.name}` : '-'
            }
            fontColor="#333D47"
            searchQuery={searchQuery}
            size="sm"
          />
          {priceType !== ProductPriceType.List && (
            <SHighlightText
              value={product.manufacturerSku || '-'}
              searchQuery={searchQuery}
              fontColor={TextColor.Grey}
              size="sm"
            />
          )}
        </ManufacturerContainer>
        <Divider />
        <PriceContainer container spacing={1}>
          {priceType === ProductPriceType.Get && selectedCustomer && (
            <PriceItem item xs={10}>
              <PriceButton
                price={product.price}
                customLoader={<CircularProgress size={20} />}
                onGetPrice={() => getProductPrice([product])}
              />
            </PriceItem>
          )}
          {priceType === ProductPriceType.GetFull && selectedCustomer && (
            <PriceItem item xs={10}>
              <PriceButton
                price={product.price}
                customLoader={<CircularProgress size={20} />}
                onGetPrice={() => getProductPrice([product])}
              />
            </PriceItem>
          )}
          {((priceType === ProductPriceType.Full && selectedCustomer) ||
            priceType === ProductPriceType.List) && (
            <Grid item xs={10}>
              {priceType === ProductPriceType.Full && selectedCustomer && (
                <>
                  {priceLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Price value={product.price} fontColor="#333D47" />
                  )}
                </>
              )}
              {priceType === ProductPriceType.List && (
                <Price
                  prefix="List: "
                  value={product.price}
                  weight="semibold"
                />
              )}
            </Grid>
          )}

          {!showEmptyGrid && <Grid item xs={10} />}

          <Grid
            item
            xs={2}
            style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <RemoveCircleIcon
              color="error"
              className={classes.removeButton}
              onClick={() => removeProduct(product)}
            />
          </Grid>
        </PriceContainer>
      </Box>
    </Container>
  );
};
